import { Reference } from "@apollo/client";
import { ItemReorderEventDetail } from "@ionic/core";
import { IonReorderGroup } from "@ionic/react";
import { MouseEvent, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { formatPhoneNumber } from "react-phone-number-input";
import { useHistory, useLocation } from "react-router";

import ActionIconButton from "@components/buttons/ActionIconButton";
import Button from "@components/buttons/Button";
import ClientVisibleTaskResource from "@components/ClientVisibleTaskResource";
import styles from "@components/ClientVisibleTaskResources.module.css";
import DateStamp from "@components/dates/DateStamp";
import DeleteConfirmationAlert from "@components/DeleteConfirmationAlert";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import Form from "@components/forms/Form";
import ResourceForm from "@components/forms/ResourceForm";
import TaskResourceFeaturesForm from "@components/forms/TaskResourceFeaturesForm";
import HotlineLink from "@components/HotlineLink";
import PageHeader from "@components/layout/PageHeader";
import Modal from "@components/Modal";
import Nbsp from "@components/Nbsp";
import PinToggle from "@components/PinToggle";
import Resource from "@components/Resource";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import ExternalLink from "@components/typography/ExternalLink";
import Heading2 from "@components/typography/Heading2";
import Heading3 from "@components/typography/Heading3";
import { openerContext } from "@context/Contexts";
import useGlobalOpener from "@hooks/useGlobalOpener";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { OpenerTypeEnum } from "@typing/Enums";
import {
  ClientRoleRoleEnum,
  GetJourneyWithPlanQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  GoalStatusEnum,
  PdfTypeEnum,
  PinResourceTypeEnum,
  ResourceDestroyDocument,
  ResourceDestroyMutation,
  ResourceDestroyMutationVariables,
  ResourceFragment,
  ResourcesReorderDocument,
  ResourcesReorderMutation,
  ResourcesReorderMutationVariables,
  ResourceUpdateDocument,
  ResourceUpdateMutation,
  ResourceUpdateMutationVariables,
  TaskFragment,
  TaskMarkReadDocument,
  TaskMarkReadMutation,
  TaskMarkReadMutationVariables
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";
import { moveArrayByIndex, resourceHasChanges, sortByPosition, sortByTitle } from "@utils/modelUtils";
import { isMobileWeb, isNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  journey: GetJourneyWithPlanQuery["journey"];
  task: TaskFragment;
};

enum ResourceViewType {
  CHART = "CHART",
  LIST = "LIST"
}

type FormData = {
  viewType: ResourceViewType;
};

const ClientVisibleTaskResources = ({ journey, task }: Props) => {
  const intl = useIntl();
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const focusResourceId = searchParams.get("resource-id");

  const resourceRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>) => {
      event.preventDefault();
      getPdfDownload({
        fetchPolicy: "no-cache",
        variables: { id: task.id, journeyId: journey.id, type: PdfTypeEnum.TASK_RESOURCES }
      }).then(response => {
        downloadFile(response.data.pdfDownload.url, "grayce-resources.pdf");
      });
    },
    [getPdfDownload, journey.id, task.id]
  );

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (focusResourceId) {
      timeout = setTimeout(() => {
        const element = resourceRefs.current[focusResourceId];
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 700);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [focusResourceId]);

  const { toggleOpener } = useContext(openerContext);

  const resourceOpener = useOpener(myExpert ? true : false);
  const resourceDeleteOpener = useOpener();
  const resourceFeaturesOpener = useOpener();
  const timeTrackingOpener = useGlobalOpener(OpenerTypeEnum.TIME_LOG);

  const [resourceBeingEdited, setResourceBeingEdited] = useState<ResourceFragment | undefined>(undefined);
  const [read, setRead] = useState(false);

  const [markTaskRead] = useMutationWithValidation<TaskMarkReadMutation, TaskMarkReadMutationVariables>(
    TaskMarkReadDocument,
    "task"
  );

  const [resourcesReorder] = useMutationWithValidation<ResourcesReorderMutation, ResourcesReorderMutationVariables>(
    ResourcesReorderDocument,
    "resource"
  );

  const [destroyResource] = useMutationWithValidation<ResourceDestroyMutation, ResourceDestroyMutationVariables>(
    ResourceDestroyDocument,
    "resource",
    {
      update: (store, { data }) => {
        if (data?.resourceDestroy?.id) {
          store.modify({
            fields: {
              resources(cachedRefs: readonly Reference[], { readField }) {
                return cachedRefs.filter(ref => data.resourceDestroy?.id !== readField("id", ref));
              }
            },
            id: store.identify({ ...task })
          });
        }
      }
    }
  );

  const handleMessageExpert = useCallback(
    (resource: ResourceFragment) => {
      const isMobile = isMobileWeb() || isNativeApp();
      toggleOpener(OpenerTypeEnum.MESSAGES, !isMobile, resource);

      if (isMobile) {
        history.push(
          journeyRoute({
            journeyId: journey.id,
            linkAfterJourney: `/messages`,
            myExpert
          })
        );
      }
    },
    [history, journey.id, myExpert, toggleOpener]
  );

  useEffect(() => {
    if (!read && myClient?.id === journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.OWNER)?.client.id) {
      markTaskRead({
        id: task.id
      });
      setRead(true);
    }
  }, [journey.clientRoles, journey.id, markTaskRead, myClient?.id, read, task.id]);

  const handleDestroy = useCallback(
    (resource: ResourceFragment) => {
      setResourceBeingEdited(resource);
      resourceDeleteOpener.open();
    },
    [resourceDeleteOpener]
  );

  const handleCreate = useCallback(() => {
    setResourceBeingEdited(undefined);
    resourceOpener.open();
  }, [resourceOpener]);

  const handleResourceSuccess = useCallback(
    (editing: boolean) => {
      setResourceBeingEdited(undefined);
      if (!editing && journey.carePlanPublishedAt) {
        timeTrackingOpener.open();
      }
    },
    [journey.carePlanPublishedAt, timeTrackingOpener]
  );

  const handleEdit = useCallback(
    (resource: ResourceFragment) => {
      setResourceBeingEdited(resource);
      resourceOpener.open();
    },
    [resourceOpener]
  );

  const handleResourceDestroy = useCallback(() => {
    if (resourceBeingEdited) {
      destroyResource({
        id: resourceBeingEdited.id
      }).then(() => {
        setResourceBeingEdited(undefined);
        resourceOpener.close();
      });
    }
  }, [destroyResource, resourceBeingEdited, resourceOpener]);

  const reorderResources = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      const newResourcesOrderIds = moveArrayByIndex(
        event,
        task.resources.filter(resource => resource.useful !== false)
      ).map(resource => resource.id);
      resourcesReorder({
        resourceIds: newResourcesOrderIds,
        taskId: task.id
      }).then(() => {
        event.detail.complete();
      });
    },
    [task.resources, task.id, resourcesReorder]
  );

  const defaultValues: FormData = { viewType: ResourceViewType.LIST };

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const viewType = form.watch("viewType");

  const viewTypeOptions = [
    { label: intl.formatMessage({ id: "pages.carePlan.task.chartView" }), value: ResourceViewType.CHART },
    { label: intl.formatMessage({ id: "pages.carePlan.task.listView" }), value: ResourceViewType.LIST }
  ];

  const sortedResources = sortByPosition(task.resources).filter(resource => resource.useful !== false);
  const sortedUnusefulResources = sortByTitle(task.resources.filter(resource => resource.useful === false));

  const generateHandleDelete = useCallback(
    (resource: ResourceFragment) => () => {
      handleDestroy(resource);
    },
    [handleDestroy]
  );

  const generateHandleEdit = useCallback(
    (resource: ResourceFragment) => () => {
      handleEdit(resource);
    },
    [handleEdit]
  );

  const [updateResource] = useMutationWithValidation<ResourceUpdateMutation, ResourceUpdateMutationVariables>(
    ResourceUpdateDocument,
    "resource"
  );

  const updateUseful = useCallback(
    (resource: ResourceFragment, useful: boolean) => {
      updateResource({
        id: resource.id,
        useful
      });
    },
    [updateResource]
  );

  const activeGoal = journey.goals?.find(goal => goal.id === task.goal?.activeGoalId);
  const resourceContainsChanges = (r: ResourceFragment) =>
    resourceHasChanges(r, activeGoal) && task.goal?.status === GoalStatusEnum.EDIT_MODE;

  const chartValueWrapperStyles = (r: ResourceFragment) =>
    `${styles.chartValueWrapper} ${resourceContainsChanges(r) ? styles.changesSides : ""}`;

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        actionButtonProps={{
          icon: "fileDownload",
          onClick: handleDownload,
          textKey: "pages.carePlan.task.resources.downloadResources"
        }}
        tagProps={
          task.category.other || task.category.categoryOption
            ? [
                {
                  color: "secondary-500",
                  text:
                    task.category.other ??
                    intl.formatMessage({ id: `models.tasks.categoryOptions.${task.category.categoryOption}` })
                }
              ]
            : []
        }
        title={task.title ?? ""}
      />

      <div className={styles.controlsRow}>
        <div className={styles.form}>
          <Form hookForm={form} onSubmit={() => {}}>
            <RadioButtonGroup<FormData>
              control={form.control}
              error={form.formState.errors.viewType}
              name="viewType"
              noSpacing
              options={viewTypeOptions}
            />
          </Form>
        </div>
        {myExpert && (
          <div className={styles.controlsRight}>
            <BodyCopy color="primary">
              <FormattedMessage
                id="pages.carePlan.due"
                values={{
                  date: <DateStamp value={task.dueTime} />
                }}
              />
            </BodyCopy>
            <div className={`do-not-print ${styles.featuresButton}`}>
              <Button fill="clear" onClick={resourceFeaturesOpener.open}>
                <IconBase icon="screwdriverWrench" slot="start" />
                <FormattedMessage id="pages.carePlan.task.resources.editFeatureCriteria" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className={styles.flexPanes}>
        <div className={myExpert ? styles.flexMainPane : styles.flexMainPaneFull}>
          {viewType === ResourceViewType.LIST ? (
            <>
              {task.resources.length > 0 ? (
                <div>
                  <IonReorderGroup
                    className={`${styles.resources} ${myExpert ? styles.fullWidth : ""}`}
                    disabled={false}
                    onIonItemReorder={reorderResources}
                  >
                    {sortedResources.map(resource => (
                      <Resource
                        carePlanPublishedAt={journey.carePlanPublishedAt}
                        forwardRef={ref => (resourceRefs.current[resource.id] = ref)}
                        journey={journey}
                        key={resource.id}
                        onDelete={handleDestroy}
                        onEdit={handleEdit}
                        resource={resource}
                        task={task}
                      />
                    ))}
                  </IonReorderGroup>
                  {sortedUnusefulResources.length > 0 && (
                    <div className={styles.unusefulContainer}>
                      <Heading2>
                        <FormattedMessage id="pages.carePlan.task.resources.markedNotUseful" />
                      </Heading2>
                      <div className={styles.unusefulDescription}>
                        <BodyCopy color="primary">
                          <FormattedMessage id="pages.carePlan.task.resources.markedNotUsefulDescription" />
                        </BodyCopy>
                      </div>
                      {sortedUnusefulResources.map(resource => (
                        <Resource
                          carePlanPublishedAt={journey.carePlanPublishedAt}
                          forwardRef={ref => (resourceRefs.current[resource.id] = ref)}
                          journey={journey}
                          key={resource.id}
                          onDelete={handleDestroy}
                          onEdit={handleEdit}
                          resource={resource}
                          task={task}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.fakeResource}>
                  <BodyCopy color="primary">
                    <FormattedMessage id="pages.carePlan.task.resources.noResources" />
                  </BodyCopy>
                </div>
              )}
            </>
          ) : (
            <div className={styles.resourceChart}>
              <div className={styles.chartRow}>
                <div className={`${styles.chartColumn} ${styles.resourceChartRowHeading}`}></div>
                {sortedResources.length > 0 && (
                  <>
                    {sortedResources.map(resource => (
                      <div className={`${styles.chartColumn}`} key={resource.id}>
                        <div
                          className={`${chartValueWrapperStyles(resource)} ${styles.chartValueWrapperTop} ${
                            resourceContainsChanges(resource) ? styles.changesTop : ""
                          }`}
                        >
                          <PinToggle
                            color="gray-500"
                            goal={task.goal}
                            journeyId={journey.id}
                            resourceId={resource.id}
                            resourceType={PinResourceTypeEnum.RESOURCE}
                          />

                          {myExpert && (
                            <div>
                              <div className={`${styles.resourceControl} ion-margin-end`}>
                                <ActionIconButton
                                  ariaLabelKey="accessibility.ariaLabels.edit"
                                  icon="edit"
                                  onClick={generateHandleEdit(resource)}
                                />
                              </div>
                              <div className={styles.resourceControl}>
                                <ActionIconButton
                                  ariaLabelKey="accessibility.ariaLabels.delete"
                                  icon="trash"
                                  onClick={generateHandleDelete(resource)}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              <div className={styles.chartRow}>
                <div className={`${styles.chartColumn} ${styles.resourceChartRowHeading}`}></div>
                {sortedResources.map(resource => (
                  <div className={styles.chartColumn} key={resource.id}>
                    <div className={chartValueWrapperStyles(resource)}>
                      <Heading3 color="primary">{resource.title}</Heading3>
                    </div>
                  </div>
                ))}
              </div>
              {sortedResources.some(resource => resource.linkTitle && resource.linkUrl) && (
                <div className={styles.chartRow}>
                  <div className={`${styles.chartColumn} ${styles.resourceChartRowHeading}`}></div>
                  {sortedResources.map(resource => (
                    <div className={styles.chartColumn} key={resource.id}>
                      <div className={chartValueWrapperStyles(resource)}>
                        {resource.linkTitle && resource.linkUrl && (
                          <div className={styles.resourceLink}>
                            <ExternalLink href={resource.linkUrl}>{resource.linkTitle}</ExternalLink>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {sortedResources.some(resource => resource.phoneNumber) && (
                <div className={styles.chartRow}>
                  <div className={`${styles.chartColumn} ${styles.resourceChartRowHeading}`}></div>
                  {sortedResources.map(resource => (
                    <div className={styles.chartColumn} key={resource.id}>
                      <div className={chartValueWrapperStyles(resource)}>
                        {resource.phoneNumber && (
                          <div>
                            <ExternalLink href={`tel:${resource.phoneNumber}`}>
                              {formatPhoneNumber(resource.phoneNumber)
                                ? formatPhoneNumber(resource.phoneNumber)
                                : resource.phoneNumber}
                            </ExternalLink>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {sortedResources.some(resource => resource.hotline) && (
                <div className={`${styles.chartRow}`}>
                  <div className={`${styles.resourceChartRowHeading} ${styles.chartColumn} ${styles.chartColumnOdd}`} />
                  {sortedResources.map(resource => (
                    <div className={styles.chartColumn} key={resource.id}>
                      <div className={chartValueWrapperStyles(resource)}>
                        {resource.hotline && (
                          <div>
                            <HotlineLink hotline={resource.hotline} />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {sortedResources.some(resource => resource.cost) && (
                <div className={`${styles.chartRow}`}>
                  <div className={`${styles.resourceChartRowHeading} ${styles.chartColumn} ${styles.chartColumnOdd}`}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id="dictionary.cost" />
                    </BodyCopy>
                  </div>
                  {sortedResources.length > 0 ? (
                    <>
                      {sortedResources.map(resource => (
                        <div className={`${styles.chartColumn} ${styles.chartColumnOdd}`} key={resource.id}>
                          <div className={chartValueWrapperStyles(resource)}>
                            {resource.cost && (
                              <>
                                <div>
                                  <Icon color="primary" icon="moneyBill" />
                                </div>

                                <BodyCopy color="primary">{resource.cost}</BodyCopy>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className={`${styles.chartColumn} ${styles.chartColumnOdd}`}></div>
                  )}
                </div>
              )}
              {sortByPosition(task.resourceFeatures).map((resourceFeature, index) => (
                <div className={`${styles.chartRow}`} key={resourceFeature.id}>
                  <div
                    className={`${styles.resourceChartRowHeading} ${styles.chartColumn} ${
                      index % 2 === 1 ? styles.chartColumnOdd : styles.chartColumnEven
                    }`}
                  >
                    <BodyCopy bold color="primary">
                      {resourceFeature.name}
                    </BodyCopy>
                  </div>

                  {sortedResources.length > 0 ? (
                    <>
                      {sortedResources.map(resource => (
                        <ClientVisibleTaskResource
                          chartValueWrapperStyles={chartValueWrapperStyles}
                          index={index}
                          journeyId={journey.id}
                          key={resource.id}
                          resource={resource}
                          resourceFeature={resourceFeature}
                        />
                      ))}
                    </>
                  ) : (
                    <div
                      className={`${styles.chartColumn} ${
                        index % 2 === 1 ? styles.chartColumnOdd : styles.chartColumnEven
                      }`}
                    ></div>
                  )}
                </div>
              ))}
              <div className={styles.chartRow}>
                <div
                  className={`${styles.chartColumn} ${styles.resourceChartRowHeading} ${
                    task.resourceFeatures.length % 2 === 1 ? styles.chartColumnOdd : styles.chartColumnEven
                  }`}
                ></div>
                {sortedResources.map(resource => (
                  <div
                    className={`${styles.chartColumn} ${styles.chartColumnBottom} ${
                      task.resourceFeatures.length % 2 === 1 ? styles.chartColumnOdd : styles.chartColumnEven
                    }`}
                    key={resource.id}
                  >
                    <div
                      className={`${chartValueWrapperStyles(resource)} ${styles.chartValueWrapperBottom} ${
                        resourceContainsChanges(resource) ? styles.changesBottom : ""
                      }`}
                    >
                      <div>
                        {!task.goal?.hasDuplicateGoalInEditMode && (
                          <>
                            <div>
                              {resource.useful === true ? (
                                <BodyCopy bold color="primary-500">
                                  <Icon color="primary-500" icon="thumbsUp" />
                                  <Nbsp />
                                  <Nbsp />
                                  <FormattedMessage id="pages.carePlan.task.resources.useful" />
                                </BodyCopy>
                              ) : (
                                <Button color="gray-500" fill="clear" onClick={() => updateUseful(resource, true)}>
                                  <IconBase icon="thumbsUp" slot="start" />
                                  <FormattedMessage id="pages.carePlan.task.resources.useful" />
                                </Button>
                              )}
                            </div>
                            <div>
                              {resource.useful === false ? (
                                <BodyCopy bold color="primary-500">
                                  <Icon color="primary-500" icon="thumbsDown" />
                                  <Nbsp />
                                  <Nbsp />
                                  <FormattedMessage id="pages.carePlan.task.resources.notUseful" />
                                </BodyCopy>
                              ) : (
                                <Button color="gray-500" fill="clear" onClick={() => updateUseful(resource, false)}>
                                  <IconBase icon="thumbsDown" slot="start" />
                                  <FormattedMessage id="pages.carePlan.task.resources.notUseful" />
                                </Button>
                              )}
                            </div>
                          </>
                        )}

                        {!myExpert && (
                          <div className={styles.messageExpertLink}>
                            <ButtonLink onClick={() => handleMessageExpert(resource)}>
                              <FormattedMessage id="pages.carePlan.task.resources.messageYourExpert" />
                            </ButtonLink>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={styles.spacing} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {myExpert && (
          <div className={styles.flexEditorPane}>
            <div className={styles.flexEditorPaneContent}>
              {resourceOpener.isOpen ? (
                <ResourceForm
                  autofocus={false}
                  journey={journey}
                  onCancel={resourceOpener.close}
                  onDelete={resourceDeleteOpener.open}
                  onSuccess={handleResourceSuccess}
                  resource={resourceBeingEdited}
                  task={task}
                />
              ) : (
                <Button expand="block" onClick={handleCreate}>
                  <FormattedMessage id="pages.carePlan.task.resources.addResource" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        opener={resourceFeaturesOpener}
        title={intl.formatMessage({
          id: "models.tasks.editResourceFeatures"
        })}
      >
        {() => (
          <TaskResourceFeaturesForm
            onCancel={resourceFeaturesOpener.close}
            onSuccess={resourceFeaturesOpener.close}
            task={task}
          />
        )}
      </Modal>

      <DeleteConfirmationAlert
        messageKey="pages.carePlan.task.resources.reallyDeleteResource"
        messageValue={resourceBeingEdited?.title}
        messageValueKey="title"
        onDelete={handleResourceDestroy}
        opener={resourceDeleteOpener}
      />
    </div>
  );
};

export default ClientVisibleTaskResources;
